// src/components/Orders/Admin/MobileOrdersView.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Stack, Button, Chip, Select, MenuItem, useTheme
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import Person from '@mui/icons-material/Person';
import Phone from '@mui/icons-material/Phone';
import AttachMoney from '@mui/icons-material/AttachMoney';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Info from '@mui/icons-material/Info';

const MobileOrdersView = ({ orders, handleStatusUpdate, getStatusColor, getStatusLabel }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const getStatusThemeColor = (status) => {
    switch (status) {
      case 'delivered':
        return theme.palette.success.main;
      case 'cancelled':
        return theme.palette.error.main;
      case 'confirmed':
        return theme.palette.primary.main;
      case 'shipped':
        return theme.palette.info.main;
      default:
        return theme.palette.warning.main; // For 'pending'
    }
  };

  const MobileOrderCard = ({ order }) => (
    <Card 
      sx={{ 
        mb: 2, 
        borderRadius: 2,
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease'
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Order Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            #{order.id.slice(0, 8)}
          </Typography>
          <Chip
            icon={
              order.status === 'pending' ? <AccessTimeIcon sx={{ fontSize: 16 }} /> :
              order.status === 'confirmed' ? <ThumbUpIcon sx={{ fontSize: 16 }} /> :
              order.status === 'shipped' ? <LocalShippingIcon sx={{ fontSize: 16 }} /> :
              order.status === 'delivered' ? <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> :
              order.status === 'cancelled' ? <HighlightOffIcon sx={{ fontSize: 16 }} /> :
              null
            }
            label={getStatusLabel(order.status)}
            color={getStatusColor(order.status)}
            size="small"
            sx={{ 
              fontWeight: 'bold',
              minWidth: '120px',
              '& .MuiChip-icon': {
                color: 'inherit'
              }
            }}
          />
        </Box>

        {/* Order Details */}
        <Stack spacing={1.5}>
          {/* Customer Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold'}}>
              {order.profiles?.full_name || 'غير محدد'}
            </Typography>
          </Box>

          {/* Phone */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold'}}>
              {order.profiles?.phone_number || 'غير محدد'}
            </Typography>
          </Box>

          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold'}}>
              {new Date(order.created_at).toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>

          {/* Amount */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {order.total_amount.toFixed(2)} جنيه
            </Typography>
          </Box>
        </Stack>

        {/* Status Update Section */}
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            تحديث حالة الطلب:
          </Typography>
          <Select
            value={order.status}
            onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
            size="small"
            fullWidth
            sx={{ 
              mb: 2, 
              fontWeight: 'bold',
              '& .MuiSelect-select': {
                color: getStatusThemeColor(order.status),
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }
            }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {value === 'pending' && <AccessTimeIcon sx={{ fontSize: 20 }} />}
                {value === 'confirmed' && <ThumbUpIcon sx={{ fontSize: 20 }} />}
                {value === 'shipped' && <LocalShippingIcon sx={{ fontSize: 20 }} />}
                {value === 'delivered' && <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                {value === 'cancelled' && <HighlightOffIcon sx={{ fontSize: 20 }} />}
                {getStatusLabel(value)}
              </Box>
            )}
          >
            <MenuItem 
              value="pending" 
              sx={{ 
                direction: 'rtl', 
                fontWeight: 'bold',
                color: theme.palette.warning.main,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 18 }} />
              قيد الانتظار
            </MenuItem>
            <MenuItem 
              value="confirmed" 
              sx={{ 
                direction: 'rtl', 
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <ThumbUpIcon sx={{ fontSize: 18 }} />
              تم التأكيد
            </MenuItem>
            <MenuItem 
              value="shipped" 
              sx={{ 
                direction: 'rtl', 
                fontWeight: 'bold',
                color: theme.palette.info.main,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <LocalShippingIcon sx={{ fontSize: 18 }} />
              تم الشحن
            </MenuItem>
            <MenuItem 
              value="delivered" 
              sx={{ 
                direction: 'rtl', 
                fontWeight: 'bold',
                color: theme.palette.success.main,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              تم التسليم
            </MenuItem>
            <MenuItem 
              value="cancelled" 
              sx={{ 
                direction: 'rtl', 
                fontWeight: 'bold',
                color: theme.palette.error.main,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <HighlightOffIcon sx={{ fontSize: 18 }} />
              ملغى
            </MenuItem>
          </Select>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Info />}
            onClick={() => navigate(`/admin/order/${order.id}`)}
            sx={{
              borderRadius: 2,
              fontWeight: 'bold',
              '& .MuiButton-startIcon': {
                marginRight: '4px',
                marginLeft: 1
              }
            }}
          >
            عرض التفاصيل
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {orders.map((order) => (
        <MobileOrderCard key={order.id} order={order} />
      ))}
    </Box>
  );
};

export default MobileOrdersView;